import { Branding, BrandingColors, BrandingFont, BrandingInput } from '@/types/branding.types';

/** Which cascade source marks a value as the editor scope's OWN saved override. */
export type OwnSource = 'team' | 'agency';

/**
 * Colors the scope owns (source === ownSource). Inherited / default values are left out
 * so empty fields stay empty in the editor (and keep resolving through the cascade).
 */
export const pickOwnColors = (branding: Branding, ownSource: OwnSource): BrandingColors => {
  const own: BrandingColors = {};
  const sources = branding.sources?.colors;
  for (const key of Object.keys(branding.colors ?? {}) as Array<keyof BrandingColors>) {
    const value = branding.colors?.[key];
    if (value && sources?.[key] === ownSource) own[key] = value;
  }
  return own;
};

/** Resolved (inherited or default) colors for fields the scope does NOT own: placeholder values. */
export const pickInheritedColors = (branding: Branding, ownSource: OwnSource): BrandingColors => {
  const inherited: BrandingColors = {};
  const sources = branding.sources?.colors;
  for (const key of Object.keys(branding.colors ?? {}) as Array<keyof BrandingColors>) {
    const value = branding.colors?.[key];
    if (value && sources?.[key] !== ownSource) inherited[key] = value;
  }
  return inherited;
};

/** Draft colors -> save payload: null clears a saved own value, empty unsaved fields are omitted. */
export const buildColorsPayload = (
  draft: BrandingColors,
  branding: Branding | undefined,
  ownSource: OwnSource
): NonNullable<BrandingInput['colors']> => {
  const payload: NonNullable<BrandingInput['colors']> = {};
  const sources = branding?.sources?.colors;
  for (const key of Object.keys(draft) as Array<keyof BrandingColors>) {
    const value = draft[key];
    if (value === undefined) continue;
    if (value.trim()) {
      payload[key] = value;
    } else if (sources?.[key] === ownSource) {
      payload[key] = null;
    }
  }
  return payload;
};

/**
 * Font -> save payload. undefined = leave unchanged. A font that merely mirrors the inherited
 * (non-own) resolved value is not sent, so it is not saved as an own override; a missing font
 * with a saved own value is cleared with null.
 */
export const buildFontPayload = (
  draft: BrandingFont | undefined,
  resolved: BrandingFont | undefined,
  source: 'team' | 'agency' | 'default' | undefined,
  ownSource: OwnSource
): BrandingFont | null | undefined => {
  if (!draft) return source === ownSource ? null : undefined;
  if (source !== ownSource && resolved && JSON.stringify(draft) === JSON.stringify(resolved)) {
    return undefined;
  }
  return draft;
};
